import AttachFile from '@mui/icons-material/AttachFile'
import Download from '@mui/icons-material/Download'
import ErrorOutline from '@mui/icons-material/ErrorOutline'
import Send from '@mui/icons-material/Send'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import IconButton from '@mui/material/IconButton'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { formatDateTimeShort } from 'common/libs/fechas'
import { selectUsuario } from 'features/users/stores/userSlice'
import i18n from 'i18next'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import useChatSolicitud from '../hooks/useChatSolicitud'

const iniciales = (nombre) =>
  (nombre || '')
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

const esOptimista = (m) => typeof m.id === 'string' && m.id.startsWith('tmp-')

/**
 * Indica si un estado de solicitud implica que el chat queda cerrado.
 * Reglas: 500 (aprobada), 600 (rechazada) e impares dentro de [203, 289].
 * Si el estado es undefined/null devolvemos null (desconocido) para que el
 * componente pueda diferenciar "cargando" de "cerrado" y no muestre el aviso
 * antes de tiempo (AC-CHATSOL-2).
 */
const calcularChatCerrado = (estado) => {
  if (estado === undefined || estado === null) return null
  if (estado === 500 || estado === 600) return true
  if (estado >= 203 && estado <= 289 && estado % 2 === 1) return true
  return false
}

const SkeletonBurbuja = () => (
  <Stack direction="row" spacing={1} alignItems="flex-start" data-testid="chat-skeleton">
    <Skeleton variant="circular" width={40} height={40} />
    <Stack sx={{ flexGrow: 1 }} spacing={0.5}>
      <Skeleton variant="rounded" width={120} height={16} />
      <Skeleton variant="rounded" width="80%" height={20} />
    </Stack>
  </Stack>
)

/**
 * Render del adjunto dentro de la burbuja de chat.
 * Pinta SIEMPRE que exista `url_fichero` o `nombre_fichero`, independiente de
 * si el mensaje es propio (emisor, AC-CHATSOL-4) o ajeno (receptor,
 * AC-CHATSOL-5). De este modo un mensaje con solo fichero nunca queda vacío.
 */
const FicheroAdjunto = ({ urlFichero, nombreFichero }) => {
  if (!urlFichero && !nombreFichero) return null
  if (urlFichero) {
    return (
      <a href={urlFichero} download={nombreFichero || true}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Download fontSize="small" />
          <Typography variant="body2">
            {nombreFichero || i18n.t('chatdwn', { defaultValue: 'Descargar' })}
          </Typography>
        </Stack>
      </a>
    )
  }
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <AttachFile fontSize="small" />
      <Typography variant="body2">{nombreFichero}</Typography>
    </Stack>
  )
}

const ChatSolicitud = ({ codigoSolicitud, origenSolicitud, puedeEscribir, estado }) => {
  const { mensajes, enviar, reintentar, marcarLeidos, refrescando } = useChatSolicitud({
    codigoSolicitud,
    origenSolicitud,
  })
  // Cuando el padre nos pasa `estado`, derivamos puedeEscribir desde él.
  // Si `estado` es undefined (carga inicial) tratamos el chat como abierto
  // para no mostrar el aviso "cerrada" antes de conocer el estado real
  // (AC-CHATSOL-2). El prop `puedeEscribir` sigue siendo soportado como
  // override explícito por compatibilidad con tests existentes.
  const chatCerradoEstado = calcularChatCerrado(estado)
  const puedeEscribirEfectivo =
    chatCerradoEstado === null ? puedeEscribir !== false : !chatCerradoEstado
  const usuarioActual = useSelector(selectUsuario)
  const usuarioWeb = usuarioActual?.usuario_web
  const [texto, setTexto] = useState('')
  const [fichero, setFichero] = useState(null)
  const observerRef = useRef(null)

  const noLeidos = useMemo(
    () =>
      mensajes
        .filter((m) => !m.leido && (!usuarioWeb || m.usuario_web !== usuarioWeb))
        .map((m) => m.id),
    [mensajes, usuarioWeb],
  )

  const observarMensaje = useCallback(
    (nodo, id) => {
      if (!nodo || !id) return
      if (!observerRef.current) {
        const pendientes = new Set()
        let timeout = null
        observerRef.current = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const mid = Number(entry.target.dataset.mid)
              if (mid) pendientes.add(mid)
            }
          })
          if (pendientes.size > 0) {
            if (timeout) clearTimeout(timeout)
            timeout = setTimeout(() => {
              const ids = Array.from(pendientes)
              pendientes.clear()
              marcarLeidos(ids)
            }, 300)
          }
        })
      }
      observerRef.current.observe(nodo)
    },
    [marcarLeidos],
  )

  useEffect(() => () => observerRef.current?.disconnect(), [])

  const handleEnviar = async () => {
    if (!texto && !fichero) return
    const textoEnvio = texto
    const ficheroEnvio = fichero
    setTexto('')
    setFichero(null)
    await enviar(textoEnvio, ficheroEnvio, usuarioActual)
  }

  const mostrarSkeleton = refrescando && mensajes.length === 0

  return (
    <Card>
      <CardHeader
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">
              {i18n.t('chatcnvs', { defaultValue: 'Conversación' })}
            </Typography>
            {noLeidos.length > 0 ? (
              <Typography variant="caption" color="primary">
                ({noLeidos.length} {i18n.t('chatmsnl', { defaultValue: 'Mensajes sin leer' })})
              </Typography>
            ) : null}
          </Stack>
        }
      />
      <CardContent>
        {mostrarSkeleton ? (
          <Stack spacing={1.5}>
            <SkeletonBurbuja />
            <SkeletonBurbuja />
          </Stack>
        ) : mensajes.length === 0 ? (
          <Typography variant="body2">
            {i18n.t('chatnoms', {
              defaultValue: 'Aún no hay mensajes en esta conversación.',
            })}
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {mensajes.map((m) => {
              const optimista = esOptimista(m) || m.enviando || m.error
              const isOwn = !!usuarioWeb && m.usuario_web === usuarioWeb
              const estiloContenedor = {
                justifyContent: isOwn ? 'flex-end' : 'flex-start',
                ...(m.enviando ? { opacity: 0.6 } : {}),
              }
              return (
                <Stack
                  key={m.id}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                  ref={(n) => (m.leido || optimista ? null : observarMensaje(n, m.id))}
                  data-mid={m.id}
                  data-testid={
                    optimista ? 'chat-mensaje-optimista' : `chat-mensaje-contenedor-${m.id}`
                  }
                  data-own={isOwn ? 'true' : 'false'}
                  style={estiloContenedor}
                >
                  {!isOwn ? (
                    <Avatar
                      src={m.url_avatar}
                      alt={m.nombre_autor || m.usuario_web}
                      data-testid={`chat-mensaje-avatar-${m.id}`}
                    >
                      {iniciales(m.nombre_autor || m.usuario_web)}
                    </Avatar>
                  ) : null}
                  <Stack
                    sx={{
                      flexGrow: isOwn ? 0 : 1,
                      maxWidth: '80%',
                      bgcolor: isOwn ? 'primary.light' : 'background.paper',
                      color: isOwn ? 'primary.contrastText' : 'text.primary',
                      borderRadius: 2,
                      px: 1.5,
                      py: 1,
                      alignItems: isOwn ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <Typography
                      variant="body2"
                      color={isOwn ? 'inherit' : 'text.primary'}
                      data-testid={`chat-mensaje-autor-${m.id}`}
                    >
                      {m.nombre_autor || m.usuario_web}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={isOwn ? 'inherit' : 'text.secondary'}
                      data-testid={`chat-mensaje-fecha-${m.id}`}
                    >
                      {m.enviando
                        ? i18n.t('chatenvg', { defaultValue: 'Enviando…' })
                        : formatDateTimeShort(m.fecha) || m.fecha}
                    </Typography>
                    {m.mensaje ? <Typography>{m.mensaje}</Typography> : null}
                    <FicheroAdjunto urlFichero={m.url_fichero} nombreFichero={m.nombre_fichero} />
                    {m.error ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ErrorOutline color="error" fontSize="small" />
                        <Typography variant="caption" color="error">
                          {i18n.t('chaterr', {
                            defaultValue:
                              'No se ha podido enviar el mensaje. Toca para reintentar.',
                          })}
                        </Typography>
                        <Button size="small" color="error" onClick={() => reintentar(m.id)}>
                          {i18n.t('chatrnt', { defaultValue: 'Reintentar' })}
                        </Button>
                      </Stack>
                    ) : null}
                  </Stack>
                </Stack>
              )
            })}
          </Stack>
        )}
        <Box sx={{ mt: 2 }}>
          {puedeEscribirEfectivo ? (
            <Stack spacing={1}>
              <TextField
                multiline
                minRows={2}
                fullWidth
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder={i18n.t('chatphmg', {
                  defaultValue: 'Escribe un mensaje',
                })}
                inputProps={{ 'aria-label': 'mensaje' }}
              />
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ flexWrap: 'wrap', rowGap: 1 }}
              >
                <IconButton component="label" aria-label="adjuntar" sx={{ flexShrink: 0 }}>
                  <AttachFile />
                  <input
                    type="file"
                    hidden
                    onChange={(e) => setFichero(e.target.files?.[0] || null)}
                  />
                </IconButton>
                <Box
                  data-testid="chat-fichero-chip"
                  sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {fichero ? (
                    <Typography
                      variant="caption"
                      noWrap
                      title={fichero.name}
                      sx={{ width: '100%' }}
                    >
                      {fichero.name}
                    </Typography>
                  ) : (
                    <Typography variant="caption" noWrap sx={{ width: '100%' }}>
                      {i18n.t('chatadj', { defaultValue: 'Adjuntar archivo' })}
                    </Typography>
                  )}
                </Box>
                <Button
                  data-testid="chat-boton-enviar"
                  variant="contained"
                  startIcon={<Send />}
                  disabled={!texto && !fichero}
                  onClick={handleEnviar}
                  style={{ flexShrink: 0 }}
                >
                  {i18n.t('chatenv', { defaultValue: 'Enviar' })}
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {i18n.t('chatcerr', {
                defaultValue: 'Esta conversación está cerrada porque la solicitud ha finalizado.',
              })}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}

export default ChatSolicitud
